import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Package, X } from "lucide-react";

import { authApi, getApiErrorMessage } from "../api";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2200,
  padding: 16,
};

const cardStyle = {
  width: "min(720px, 100%)",
  maxHeight: "88vh",
  overflow: "auto",
  background: "#fff",
  borderRadius: 20,
  boxShadow: "0 20px 60px rgba(15,23,42,.24)",
  padding: 24,
};

const fieldStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  padding: "10px 12px",
  fontSize: 13,
  color: "#0f172a",
  background: "#fff",
};

const defaultForm = (totalWeightKg) => ({
  fulfillment_mode: "LOGISTICS",
  logistics_provider_id: "",
  service_type: "STANDARD",
  tracking_number: "",
  estimated_delivery_date: "",
  total_weight_g: totalWeightKg ? Number(totalWeightKg) * 1000 : "",
  insurance_opted: false,
  insurance_amount: "",
  shipping_cost: "",
  dispatch_notes: "",
});

export default function LogisticsModal({ open, onClose, onConfirm, totalWeightKg = 0, row }) {
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(defaultForm(totalWeightKg));

  useEffect(() => {
    if (!open) return;
    setForm(defaultForm(totalWeightKg));
    setError("");
    setLoadingProviders(true);
    authApi.get("/logistics/providers")
      .then((res) => setProviders(res.data?.data || []))
      .catch((err) => {
        setProviders([]);
        setError(getApiErrorMessage(err, "Failed to load logistics providers."));
      })
      .finally(() => setLoadingProviders(false));
  }, [open, totalWeightKg]);

  const selectedProvider = useMemo(
    () => providers.find((provider) => provider.id === form.logistics_provider_id),
    [providers, form.logistics_provider_id],
  );
  const isCustomerPickup = form.fulfillment_mode === "CUSTOMER_PICKUP";

  const serviceTypes = selectedProvider?.service_types?.length ? selectedProvider.service_types : ["STANDARD", "EXPRESS", "PREMIUM"];

  useEffect(() => {
    if (!selectedProvider) return;
    if (!serviceTypes.includes(form.service_type)) {
      setForm((prev) => ({ ...prev, service_type: serviceTypes[0] || "STANDARD" }));
    }
  }, [selectedProvider, serviceTypes, form.service_type]);

  if (!open) return null;

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setError("");
    if (!isCustomerPickup && !form.logistics_provider_id) {
      setError("Please select a logistics provider.");
      return;
    }
    if (!isCustomerPickup && !form.tracking_number.trim()) {
      setError("Tracking number is required.");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({
        ...form,
        tracking_number: form.tracking_number.trim(),
        total_weight_kg: form.total_weight_g === "" ? "" : Number(form.total_weight_g) / 1000,
      });
      onClose?.();
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to confirm dispatch."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(event) => event.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 18 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
              <Package size={22} />
              Logistics Dispatch
            </div>
            <div style={{ marginTop: 4, fontSize: 13, color: "#64748b" }}>
              {row?.design_name || row?.design_code || "Selected dispatch"} · collect shipping details and generate the label in one step.
            </div>
          </div>
          <button onClick={onClose} style={{ ...fieldStyle, width: 42, padding: 8, cursor: "pointer" }} aria-label="Close logistics modal">
            <X size={16} />
          </button>
        </div>

        {error ? <div style={{ marginBottom: 14, border: "1px solid #fecaca", background: "#fff1f2", color: "#b91c1c", padding: "10px 12px", borderRadius: 12 }}>{error}</div> : null}

        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Dispatch Method</span>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => update("fulfillment_mode", "LOGISTICS")}
                style={{ border: `1px solid ${form.fulfillment_mode === "LOGISTICS" ? "#0f172a" : "#cbd5e1"}`, borderRadius: 999, background: form.fulfillment_mode === "LOGISTICS" ? "#0f172a" : "#fff", color: form.fulfillment_mode === "LOGISTICS" ? "#fff" : "#0f172a", padding: "10px 14px", fontWeight: 800, cursor: "pointer" }}
              >
                Courier / Logistics
              </button>
              <button
                type="button"
                onClick={() => update("fulfillment_mode", "CUSTOMER_PICKUP")}
                style={{ border: `1px solid ${form.fulfillment_mode === "CUSTOMER_PICKUP" ? "#0f172a" : "#cbd5e1"}`, borderRadius: 999, background: form.fulfillment_mode === "CUSTOMER_PICKUP" ? "#0f172a" : "#fff", color: form.fulfillment_mode === "CUSTOMER_PICKUP" ? "#fff" : "#0f172a", padding: "10px 14px", fontWeight: 800, cursor: "pointer" }}
              >
                Customer Pickup
              </button>
            </div>
          </div>

          {!isCustomerPickup ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 14 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Logistics Provider</span>
              <select
                value={form.logistics_provider_id}
                onChange={(e) => update("logistics_provider_id", e.target.value)}
                style={fieldStyle}
                disabled={loadingProviders}
              >
                <option value="">{loadingProviders ? "Loading providers..." : "Select provider"}</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>{provider.provider_name} ({provider.provider_code})</option>
                ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Service Type</span>
              <select value={form.service_type} onChange={(e) => update("service_type", e.target.value)} style={fieldStyle}>
                {serviceTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
          </div>
          ) : (
            <div style={{ border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1e3a8a", padding: "12px 14px", borderRadius: 12, fontSize: 13, fontWeight: 600 }}>
              Customer Pickup selected. No courier provider or tracking number is required. A pickup dispatch document will still be generated.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 14 }}>
            {!isCustomerPickup ? (
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Tracking Number</span>
              <input value={form.tracking_number} onChange={(e) => update("tracking_number", e.target.value)} placeholder="BD123456789IN" style={fieldStyle} />
            </label>
            ) : (
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Pickup Reference</span>
              <input value={form.tracking_number} onChange={(e) => update("tracking_number", e.target.value)} placeholder="Optional pickup note / gate pass no." style={fieldStyle} />
            </label>
            )}

            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>{isCustomerPickup ? "Pickup / Handover Date" : "Estimated Delivery"}</span>
              <input type="date" value={form.estimated_delivery_date} onChange={(e) => update("estimated_delivery_date", e.target.value)} style={fieldStyle} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isCustomerPickup ? "1fr" : "repeat(3,minmax(0,1fr))", gap: 14 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Weight (grams)</span>
              <input type="number" min="0" step="0.001" value={form.total_weight_g} onChange={(e) => update("total_weight_g", e.target.value)} style={fieldStyle} />
            </label>

            {!isCustomerPickup ? (
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Shipping Cost (Rs.)</span>
                <input type="number" min="0" step="0.01" value={form.shipping_cost} onChange={(e) => update("shipping_cost", e.target.value)} style={fieldStyle} />
              </label>
            ) : null}

            {!isCustomerPickup ? (
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Insurance Amount (Rs.)</span>
                <input type="number" min="0" step="0.01" value={form.insurance_amount} onChange={(e) => update("insurance_amount", e.target.value)} style={fieldStyle} disabled={!form.insurance_opted} />
              </label>
            ) : null}
          </div>

          {!isCustomerPickup ? (
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#334155" }}>
              <input type="checkbox" checked={form.insurance_opted} onChange={(e) => update("insurance_opted", e.target.checked)} />
              Opt for shipment insurance
            </label>
          ) : null}

          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Dispatch Notes</span>
            <textarea rows={4} value={form.dispatch_notes} onChange={(e) => update("dispatch_notes", e.target.value)} placeholder={isCustomerPickup ? "Pickup remarks, person collecting, ID reference..." : "Special handling notes, insurance remarks, packaging notes..."} style={{ ...fieldStyle, resize: "vertical" }} />
          </label>
        </div>

        <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{ ...fieldStyle, width: "auto", padding: "10px 14px", cursor: "pointer", fontWeight: 700 }}>Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{ border: 0, borderRadius: 12, background: "#15803d", color: "#fff", padding: "11px 18px", cursor: submitting ? "not-allowed" : "pointer", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 8, opacity: submitting ? 0.7 : 1 }}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Package size={16} />}
            Generate Label & Dispatch
          </button>
        </div>
      </div>
    </div>
  );
}
